import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useUpdateTrip } from 'api/trips/use-update-trip.ts';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard.tsx';
import { formatDate } from 'shared/utils/time/format-date';
import { isTripStarted } from 'shared/utils/trips/is-trip-started';
import { EFormDrawerWithButtonIconType, FormDrawerWithButton } from 'components/common/form-drawer-with-button';
import type { ITripFormFields } from 'components/trips/trip-form';
import { TripForm } from 'components/trips/trip-form';
import type { TCreateTripReq } from '../../../api/swagger';
import type { ITripEditProps } from './types';

const TripEditComponent: React.FC<ITripEditProps> = (props) => {
  const { trip } = props;

  const { t } = useTranslation();

  const { mutateAsync: updateTrip, isLoading } = useUpdateTrip(trip.id);

  const onFinish = async (values: ITripFormFields) => {
    const trailerPlateValue = values.trailerPlate || null;
    const apiVariables: TCreateTripReq = {
      soldToPartners: values.soldToPartners,
      currency: values.currency,
      costPrice: values.costPrice,
      estimatedStartDate: values.estimatedDate[0] && formatDate(values.estimatedDate[0]),
      estimatedEndDate: values.estimatedDate[1] && formatDate(values.estimatedDate[1]),
      ...(values.soldToPartners
        ? {
            carrierId: values.carrier?.value,
            contactId: values.contact?.value,
          }
        : {
            truckId: values.truckId,
            trailerId: values.trailerId ?? null,
            trailerPlate: values.trailerId ? null : trailerPlateValue,
          }),
    };

    await updateTrip(apiVariables);
  };

  const initialValues: ITripFormFields = {
    ...trip,
    estimatedDate: [dayjs(trip.estimatedStartDate), dayjs(trip.estimatedEndDate)],
    carrier: trip.carrier
      ? {
          value: trip.carrier.id,
          label: trip.carrier.shortName || trip.carrier?.name,
        }
      : undefined,

    contact: trip.contact
      ? {
          value: trip.contact.id,
          label: trip.contact.name,
        }
      : undefined,
  };

  const hasTripStarted = isTripStarted(trip.status);

  return (
    <FormDrawerWithButton
      formComponent={TripForm}
      formProps={{ initialValues, isTripStarted: hasTripStarted }}
      title={t('trip:edit_trip')}
      buttonLabel={t('common:edit')}
      iconType={EFormDrawerWithButtonIconType.EDIT}
      isLoading={isLoading}
      onFinish={onFinish}
    />
  );
};

export const TripEdit = withRoleGuard<ITripEditProps>(TripEditComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.transport_manager,
]);
